/**
 * Quick-lead row — visually identical to LeadRow except:
 *   • No strength up/down arrows (no left color strip)
 *   • No status chips. Instead, the "status row" surfaces last-contact
 *     tracking: how many emails / calls / texts have been logged plus
 *     each-channel quick-log buttons.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, MessageSquare, ArrowRightLeft, Trash2, DollarSign, Calendar, X, UserPlus } from 'lucide-react';
import { updateQuickLead } from '@/lib/api';
import { toast } from 'sonner';

const stop = (e) => { e.stopPropagation(); };

function todayIso() { return new Date().toISOString().split('T')[0]; }

function relTime(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const diff = Date.now() - then.getTime();
  const day = 1000 * 60 * 60 * 24;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`;
  return `${Math.floor(diff / (30 * day))}mo ago`;
}

const CHANNELS = [
  { id: 'emailed_dates', label: 'Email', Icon: Mail, color: 'text-sky-700 hover:bg-sky-50' },
  { id: 'called_dates', label: 'Call', Icon: Phone, color: 'text-emerald-700 hover:bg-emerald-50' },
  { id: 'texted_dates', label: 'Text', Icon: MessageSquare, color: 'text-violet-700 hover:bg-violet-50' },
];

export function QuickLeadRow({ lead, unitMap, onOpenDetail, onMoveToCurrent, onDelete, onConvert, onUpdated }) {
  const unitNames = (lead.best_unit_ids || [])
    .map((uid) => unitMap[uid]?.unit_number)
    .filter(Boolean);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);

  const lastContactRel = useMemo(() => relTime(lead.last_contact_date), [lead.last_contact_date]);

  const logContact = async (field) => {
    setBusyChannel(field);
    try {
      const today = todayIso();
      const next = [...(lead[field] || []), today];
      await updateQuickLead(lead.id, { [field]: next, last_contact_date: today });
      onUpdated && onUpdated();
    } catch { toast.error('Failed to log contact'); }
    finally { setBusyChannel(null); }
  };

  return (
    <div
      className="relative flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-slate-50/50 cursor-pointer transition-colors"
      data-testid="quick-leads-table-row"
      onClick={() => onOpenDetail && onOpenDetail(lead)}
    >
      <div className="absolute left-0 top-0 h-full w-1 rounded-l bg-slate-300" />

      <div className="pl-3 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{lead.name || <span className="italic text-slate-400">Untitled</span>}</span>
          {lead.source && <span className="text-xs text-muted-foreground">via {lead.source}</span>}
          {(lead.price_offered || lead.budget) && (
            <Badge variant="outline" className="text-[10px] gap-0.5 tabular-nums">
              <DollarSign className="h-2.5 w-2.5" />{lead.price_offered ? parseFloat(lead.price_offered).toLocaleString() : lead.budget}
            </Badge>
          )}
          {lead.desired_start_date && lead.desired_end_date && (
            <span className="text-xs text-muted-foreground">{lead.desired_start_date} — {lead.desired_end_date}</span>
          )}
          {unitNames.length > 0 && (
            <div className="flex gap-1">
              {unitNames.map((n, i) => (
                <Badge key={i} variant="secondary" className="text-[10px]">{n}</Badge>
              ))}
            </div>
          )}
        </div>

        {/* Contact-tracking cluster (replaces the status chip row from LeadRow) */}
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {CHANNELS.map(({ id, label, Icon, color }) => {
            const count = (lead[id] || []).length;
            return (
              <button
                key={id}
                type="button"
                onClick={(e) => { stop(e); logContact(id); }}
                disabled={busyChannel === id}
                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 ${color} disabled:opacity-50`}
                title={`Log a ${label.toLowerCase()} today`}
                data-testid={`quick-row-log-${id}-${lead.id}`}
              >
                <Icon className="h-2.5 w-2.5" />
                <span>{label}</span>
                {count > 0 && <span className="font-semibold tabular-nums">·{count}</span>}
              </button>
            );
          })}
          {lastContactRel && (
            <span className="text-[10px] text-slate-500">
              Last contact <span className="font-medium tabular-nums">{lastContactRel}</span>
            </span>
          )}
        </div>

        {lead.notes && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{lead.notes}</p>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        {!lead.converted_to_tenant && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-slate-600 hover:bg-slate-100" onClick={(e) => { stop(e); onConvert(lead); }} title="Convert to tenant" data-testid={`quick-lead-convert-${lead.id}`}>
            <UserPlus className="h-3 w-3" />
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-slate-600 hover:bg-slate-100" onClick={(e) => { stop(e); onMoveToCurrent(lead); }} title="Move to Current Leads" data-testid={`quick-lead-move-current-${lead.id}`}>
          <ArrowRightLeft className="h-3 w-3" />
        </Button>
        {confirmDel ? (
          <div className="flex items-center gap-0.5">
            <Button size="sm" variant="destructive" className="h-7 text-[11px] px-2" onClick={(e) => { stop(e); setConfirmDel(false); onDelete(lead.id); }} data-testid={`quick-lead-del-yes-${lead.id}`}>Delete</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={(e) => { stop(e); setConfirmDel(false); }}><X className="h-3 w-3" /></Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={(e) => { stop(e); setConfirmDel(true); }} data-testid={`quick-lead-del-${lead.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Tiny showings/calendar hint icon when there's an upcoming desired_start_date */}
      {lead.desired_start_date && new Date(lead.desired_start_date) >= new Date(new Date().toISOString().split('T')[0]) && (
        <Calendar className="absolute top-2 right-2 h-3 w-3 text-slate-300" />
      )}
    </div>
  );
}
